/**
 * Google Analytics 4 (gtag.js) event helpers.
 * Events use GA4 recommended names where applicable for better reporting.
 */

declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config' | 'js',
      targetId: string,
      config?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

const GA_MEASUREMENT_ID = 'G-NZV28BDTR0';

function gtagSafe(
  ...args: [command: 'event' | 'config' | 'js', targetId: string, config?: Record<string, unknown>]
) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

/** Send a custom or standard GA4 event */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean | undefined>
) {
  const clean = params
    ? Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined)
      ) as Record<string, string | number | boolean>
    : undefined;
  gtagSafe('event', eventName, clean);
}

/** Virtual page view for SPA (use on route change) */
export function trackPageView(path: string, title?: string) {
  gtagSafe('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title || document.title,
  });
}

// --- Recommended GA4 events ---

export function trackLogin(method: 'email' | 'google') {
  trackEvent('login', { method });
}

export function trackSignUp(method: string) {
  trackEvent('sign_up', { method });
}

export function trackWaitlistJoin() {
  trackEvent('waitlist_join');
}

export function trackCreateWorkspace() {
  trackEvent('create_workspace');
}

export function trackCreateProject() {
  trackEvent('create_project');
}

export function trackStartChat(projectId?: string) {
  trackEvent('start_chat', projectId ? { project_id: projectId } : undefined);
}

export function trackSendMessage(hasAttachments?: boolean) {
  trackEvent('send_message', hasAttachments !== undefined ? { has_attachments: hasAttachments } : undefined);
}

export function trackCreateDoc() {
  trackEvent('create_doc');
}

export function trackBeginCheckout(planName: string, valueCents?: number) {
  trackEvent('begin_checkout', {
    plan_name: planName,
    ...(valueCents !== undefined && { value: valueCents / 100 }),
    currency: 'BRL',
  });
}

export function trackPurchase(planName: string, valueCents: number) {
  trackEvent('purchase', {
    plan_name: planName,
    value: valueCents / 100,
    currency: 'BRL',
  });
}

export function trackInviteMember() {
  trackEvent('invite_member');
}
